import { Option } from 'components/Forms/MultiSelectField';
import { useMemo, useState } from 'react';
import { Form, FormSpy } from 'react-final-form';

import { type GroupBy } from 'services/donationExportApi';
import { type SelectedDateRange } from 'services/exports/donationExportFlow';
import { useGetTransactionsDistinctRecipientsQuery } from 'services/transactionsDistinctRecipientsApi';
import { DashboardScreen, trackEvent, trackReportExported } from 'helpers/mixpanelEvents';
import { toDateString } from 'helpers/date';
import { dateRangeOptions } from './constants';
import { ReportOptionsDateSelector } from './reportOptionsDateSelector';
import { hasInvalidClientId } from './utils';
import RecipientsSelector from './recipientsSelector';

interface Props {
  clientId: number;
  title: string;
  itemName: string;
  includeRecipientsSelector?: boolean;
  groupBy?: GroupBy;
  runReport: (
    clientId: number,
    dateRange: SelectedDateRange,
    recipientsSelected: string[],
    groupBy?: GroupBy
  ) => void;
  exportButtonEnabled: boolean;
  screen: DashboardScreen;
}

export const ReportOptions = ({
  clientId,
  title,
  itemName,
  includeRecipientsSelector = false,
  groupBy = undefined,
  runReport,
  exportButtonEnabled,
  screen,
}: Props) => {
  const initialDateRange = useMemo<SelectedDateRange>(
    () => ({
      start: dateRangeOptions[0].startDate,
      end: dateRangeOptions[0].endDate,
    }),
    []
  );

  const [dateRange, setDateRange] = useState<SelectedDateRange>(initialDateRange);
  const [recipientsSelected, setRecipientsSelected] = useState<Option[]>([]);

  const { data: recipientsForClientInTimeframe } = useGetTransactionsDistinctRecipientsQuery(
    {
      start_date: dateRange.start.toISOString(),
      end_date: dateRange.end.toISOString(),
      client_id: clientId,
    },
    { skip: !includeRecipientsSelector || hasInvalidClientId(clientId) }
  );

  const recipientOptions = useMemo(() => {
    if (!(recipientsForClientInTimeframe || {}).recipients) {
      return [];
    }

    const recipients = structuredClone(recipientsForClientInTimeframe?.recipients || []);

    recipients.sort(function (a, b) {
      return a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase());
    });

    return (
      recipients.map(recipient => ({
        value: recipient,
        label: recipient,
      })) || []
    );
  }, [recipientsForClientInTimeframe]);

  const recipientsResetLink = () => (
    <button
      type="button"
      onClick={() => {
        trackEvent('Recipient Filter Reset', { screen });
        setRecipientsSelected([]);
      }}
      className="btn-link-compact link-text color-dark-gray"
    >
      Reset
    </button>
  );

  const recipientsLabel = useMemo(() => {
    if (!recipientOptions || recipientOptions.length === 0) {
      return 'No Recipients Available';
    }

    if (!recipientsSelected || recipientsSelected.length === 0) {
      return `All Recipients (${recipientOptions.length})`;
    }

    if (recipientsSelected.length === 1) {
      return (
        <span>
          {recipientsSelected[0].label} {recipientsResetLink()}
        </span>
      );
    }

    return (
      <span>
        `Selected Recipients ({recipientsSelected.length})` {recipientsResetLink()}
      </span>
    );
  }, [recipientOptions, recipientsSelected]);

  const initialFormValues = useMemo(
    () => ({ date_range: initialDateRange, recipients_selected: [] }),
    [initialDateRange]
  );

  return (
    <Form
      initialValues={initialFormValues}
      onSubmit={async values => {
        if (hasInvalidClientId(clientId)) return;
        const selectedRange = values.date_range ?? initialDateRange;
        const recipientsSelected = (values.recipients_selected || []).map((r: Option) => r.value);
        trackReportExported({
          screen,
          report_type: includeRecipientsSelector ? 'detailed_recipient' : 'summary',
          start_date: toDateString(selectedRange.start),
          end_date: toDateString(selectedRange.end),
          recipient_names: recipientsSelected,
          recipient_count: recipientsSelected.length,
        });
        runReport(clientId, selectedRange, recipientsSelected, groupBy);
      }}
      render={({ handleSubmit }) => {
        return (
          <form onSubmit={handleSubmit} noValidate>
            <FormSpy
              subscription={{ values: true }}
              onChange={({ values }) => {
                setDateRange(values?.date_range ?? initialDateRange);
              }}
            />
            <div className="card report-options-card">
              <div className="card-header report-options-head">
                <div className="report-options-subhead">
                  <span className="h4-text">{title}</span>
                  <span className="body-text">
                    This report shows a streamlined view of your most recent {itemName}.
                  </span>
                </div>
                <button
                  type="submit"
                  className="btn-inkBlue btn btn-secondary report-options-export-button"
                  disabled={hasInvalidClientId(clientId) || !exportButtonEnabled}
                >
                  Export
                </button>
              </div>
              <div className="card-body">
                <div>
                  <div className="report-options-body">
                    <ReportOptionsDateSelector
                      name="date_range"
                      dateRangeOption={dateRangeOptions[0]}
                    />
                    {includeRecipientsSelector && (
                      <RecipientsSelector
                        recipientOptions={recipientOptions}
                        recipientsSelected={recipientsSelected}
                        setRecipientsSelected={setRecipientsSelected}
                        recipientsLabel={recipientsLabel}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        );
      }}
    />
  );
};
export default ReportOptions;
